/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { cn } from '@/shared/ui/utils/tailwind';
import HousienBrandLogo from '@/shared/ui/components/basic/logos/HousienBrandLogo';

interface CornerPillProps {
  visible: boolean;
  text: string;
  leaving?: boolean;
  className?: string;
}

const R = 10;
const CIRC = 2 * Math.PI * R;

const CornerPill: React.FC<CornerPillProps> = ({
  visible,
  text,
  leaving = false,
  className,
}) => {
  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes rl7-spin { to { transform: rotate(360deg); } }
        .rl7-spin { animation: rl7-spin 1.2s linear infinite; transform-origin: 14px 14px; }
        @keyframes rl7-pill-in {
          0%   { opacity:0; transform:translateY(12px) scale(0.9); }
          100% { opacity:1; transform:translateY(0) scale(1); }
        }
        .rl7-pill-in { animation: rl7-pill-in 0.4s cubic-bezier(0.34,1.3,0.64,1) both; }
        @keyframes rl7-pill-out {
          to { opacity:0; transform:translateY(10px) scale(0.92); }
        }
        .rl7-pill-out { animation: rl7-pill-out 0.4s cubic-bezier(0.4,0,0.6,1) both; }
      `}</style>

      <div
        data-testid="corner-pill"
        role="status"
        className={cn(
          'fixed right-4 z-30',
          'flex items-center gap-2.5 pl-2.5 pr-3.5 py-2',
          'rounded-full shadow-lg border border-(--border-subtle) bg-(--surface-card)',
          leaving ? 'rl7-pill-out' : 'rl7-pill-in',
          className,
        )}
        style={{
          backdropFilter: 'blur(8px)',
          bottom: 'calc(var(--bottom-nav-height, 0px) + 1.5rem)',
        }}
      >
        {/* Mini SVG spinner */}
        <div className="relative shrink-0" style={{ width: 28, height: 28 }}>
          <svg width="28" height="28" viewBox="0 0 28 28" style={{ overflow: 'visible' }}>
            {/* Track */}
            <circle
              cx="14" cy="14" r={R}
              fill="none"
              stroke="color-mix(in srgb, var(--seed-base) 15%, transparent)"
              strokeWidth="2"
            />
            {/* Spinner arc */}
            <circle
              className="rl7-spin"
              cx="14" cy="14" r={R}
              fill="none"
              stroke="var(--seed-base)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={`${CIRC * 0.65} ${CIRC * 0.35}`}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <HousienBrandLogo variant="icon" height={14} />
          </div>
        </div>

        <span className="text-xs text-(--text-secondary) whitespace-nowrap">
          {text}
        </span>
      </div>
    </>
  );
};

export default CornerPill;
